import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'

interface ProgressStepsProps {
  callId: Id<'calls'>
}

type Step = 'introduction' | 'features' | 'medication-setup' | 'consent'

interface StepInfo {
  id: Step
  label: string
  shortLabel: string
  order: number
}

const STEPS: StepInfo[] = [
  { id: 'introduction', label: 'Introduction', shortLabel: 'Intro', order: 1 },
  { id: 'features', label: 'Features', shortLabel: 'Features', order: 2 },
  { id: 'medication-setup', label: 'Medication Setup', shortLabel: 'Meds', order: 3 },
  { id: 'consent', label: 'Notification', shortLabel: 'Notify', order: 4 },
]

/**
 * Compact horizontal progress indicator with dots
 * Mobile-friendly: shows dots with short labels
 */
export function ProgressSteps({ callId }: ProgressStepsProps) {
  const call = useQuery(api.calls.getCallById, { callId })

  const currentStep = call?.currentStep as Step | undefined
  const currentOrder = STEPS.find(s => s.id === currentStep)?.order || 0

  const getStepState = (step: StepInfo): 'completed' | 'active' | 'pending' => {
    if (!currentStep) return 'pending'
    if (step.order < currentOrder) return 'completed'
    if (step.id === currentStep) return 'active'
    return 'pending'
  }

  return (
    <div style={containerStyles}>
      {STEPS.map((step, index) => {
        const state = getStepState(step)
        const isLast = index === STEPS.length - 1

        return (
          <div key={step.id} style={stepItemStyles}>
            {/* Dot */}
            <div style={getDotStyles(state)}>
              {state === 'completed' ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M10 3L4.5 8.5L2 6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : state === 'active' ? (
                <div style={activeDotInnerStyles} />
              ) : null}
            </div>

            {/* Label */}
            <span style={getLabelStyles(state)}>{step.shortLabel}</span>

            {/* Connector line */}
            {!isLast && <div style={getConnectorStyles(state)} />}
          </div>
        )
      })}
    </div>
  )
}

// Styles
const containerStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0',
  padding: '0',
}

const stepItemStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
}

const getDotStyles = (state: 'completed' | 'active' | 'pending'): React.CSSProperties => {
  const baseStyles: React.CSSProperties = {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.3s ease',
  }

  if (state === 'completed') {
    return {
      ...baseStyles,
      backgroundColor: '#10b981',
    }
  }

  if (state === 'active') {
    return {
      ...baseStyles,
      backgroundColor: '#3b82f6',
      boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.2)',
    }
  }

  return {
    ...baseStyles,
    backgroundColor: '#e5e7eb',
  }
}

const activeDotInnerStyles: React.CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'white',
}

const getLabelStyles = (state: 'completed' | 'active' | 'pending'): React.CSSProperties => {
  const baseStyles: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: state === 'active' ? 600 : 500,
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
  }

  if (state === 'completed') {
    return { ...baseStyles, color: '#10b981' }
  }

  if (state === 'active') {
    return { ...baseStyles, color: '#3b82f6' }
  }

  return { ...baseStyles, color: '#9ca3af' }
}

const getConnectorStyles = (state: 'completed' | 'active' | 'pending'): React.CSSProperties => {
  return {
    width: '24px',
    height: '2px',
    backgroundColor: state === 'completed' ? '#10b981' : '#e5e7eb',
    marginLeft: '6px',
    marginRight: '6px',
    transition: 'all 0.3s ease',
  }
}
